import type { StoredSettings } from "./CanvasSettingsState";
import type { ScaleConfig } from "@/types/legend.types";
import { Logger } from "../logger";
import _ from "lodash";
import { Facade } from "@/drawing";
import { reactive, toRaw } from "vue";
import type { Textstyle } from "@/types/legend.types";
import NoteMenuState from "./NoteMenuState";
import CanvasSettingsState from "./CanvasSettingsState";
import { DEFAULT_SCALE } from "@/defaultLegend";
import { getFontForFileName } from "@/config/fonts";

type ForcedSize = "" | number;

export type ScaleMenuState = {
  drawingId: number;
  scales: ScaleConfig[];
  activeScale: null | ScaleConfig;
  isTextsizeForced: boolean;
  forcedSize: ForcedSize;
  storedSettings: StoredSettings | null;

  initialize: (
    drawingId: number,
    storedSettings: StoredSettings | null,
  ) => void;
  onDrawingOpen: (userScales: ScaleConfig[]) => void;
  onLegendChange: (userScales: ScaleConfig[]) => void;
  setActiveScale: (scale: ScaleConfig) => void;
  selectScale: (scale: ScaleConfig) => void;
  hasForcedSize: () => boolean;
  forcedSizeOf: (scale: ScaleConfig) => ForcedSize;
  activeScaleSize: () => number | undefined;
  getDrawingScales: () => ScaleConfig[];
  forceTextsize: () => void;
  // Compute the active scale and replace textsize if it is forced
  computeActiveScale: () => null | ScaleConfig;
  updateOrCreateTextstyle: (textstyle: Textstyle) => void;
};

export default reactive<ScaleMenuState>({
  //First element contains scales in drawing, second scales that are not in drawing and in activeLegend scales
  drawingId: -1,
  /**
   * 0: list of texstyles already inserted in the drawing
   * 1: list of scales defined by user in the legend (minus the ones already in it)
   */
  scales: [],
  activeScale: null,
  isTextsizeForced: false,
  forcedSize: "",
  storedSettings: null,
  initialize(drawingId: number, storedSettings: StoredSettings | null) {
    this.drawingId = drawingId;
    this.storedSettings = storedSettings;
  },
  /**
   * Init the height of textstyles in drawing,
   * update the scales[0] and scales[1]
   * reactivate the previously used scale
   * @param userScales the scale that has been defined in the user legend
   */
  onDrawingOpen(userScales: ScaleConfig[]) {
    this.scales = userScales; // init scales with all user scales;

    // reactivate the last used scale before drawing was closed

    if (!this.storedSettings || !this.storedSettings.scale) {
      Logger.warn(
        `CanvasSettingsState.ts : can't find stored scale ${JSON.stringify(
          this.storedSettings,
        )}`,
      );
    }

    // find the one that was stored as active
    let scale = this.scales.find(
      (scale) => scale.name == this.storedSettings?.scale?.name,
    );
    if (!scale) {
      Logger.info(
        `CanvasSettingsState.ts : can't find stored scale in drawing scales, setting scale to ${JSON.stringify(
          this.scales[0],
        )}`,
      );
      if (this.scales[0]) {
        scale = this.scales[0];
      } else {
        scale = DEFAULT_SCALE;
      }
    }
    this.isTextsizeForced =
      this.storedSettings?.scale?.isTextsizeForced || false;
    this.forcedSize = this.storedSettings?.scale?.forcedSize || "";
    if (scale) this.setActiveScale(scale);
  },
  /**
   * On legend change, need to reset scales, first scale is selected by default.
   */
  onLegendChange(userScales: ScaleConfig[]) {
    this.scales = userScales; // init scales with all user scales;
    if (this.scales[0]) this.setActiveScale(this.scales[0]);
  },
  /**
   * Activate the scale and store it to storage, update textstyle to match the scale selected
   * @param scale new scale selected
   */
  setActiveScale(scale: ScaleConfig) {
    this.activeScale = scale;
    this.storedSettings = _.set(
      this.storedSettings || {},
      "scale.name",
      scale.name,
    ) as StoredSettings;
    if (scale.props.textstyle)
      this.updateOrCreateTextstyle(scale.props.textstyle);

    // Update the computed note config to apply the new text size
    NoteMenuState.updateComputedNoteConfig();
    CanvasSettingsState.updateAndPersist();
  },
  /**
   * Choix explicite d'un style par l'utilisateur. Comme un style dans Word, il
   * reprend la main sur la mise en forme directe : la hauteur forcée, qui
   * l'emporterait silencieusement sur la hauteur du style, est relâchée.
   */
  selectScale(scale: ScaleConfig) {
    if (this.isTextsizeForced || this.forcedSize !== "") {
      this.isTextsizeForced = false;
      this.forcedSize = "";
      this.forceTextsize();
    }
    this.setActiveScale(scale);
  },
  /** Une hauteur forcée est active : elle remplace celle du style actif. */
  hasForcedSize(): boolean {
    return this.isTextsizeForced && this.forcedSize !== "";
  },
  /** Hauteur forcée qui remplace celle de cette échelle (seule l'active en a une). */
  forcedSizeOf(scale: ScaleConfig): ForcedSize {
    const isActive = this.activeScale?.name === scale.name;
    return isActive && this.hasForcedSize() ? this.forcedSize : "";
  },
  /** Hauteur du style actif : point de départ d'une hauteur forcée. */
  activeScaleSize(): number | undefined {
    const props = this.activeScale?.props;
    return props?.textsize ?? props?.textstyle?.size;
  },
  /**
   * Retrieve the scales that are in the drawing
   */
  getDrawingScales(): ScaleConfig[] {
    if (!Facade.isInitialized) return [];
    const drawingStyles = Facade.textStyles.getAll();
    const scales: ScaleConfig[] = [];
    for (const style of drawingStyles) {
      scales.push({
        id: style.name,
        name: style.name,
        props: {
          textstyle: style,
        },
      });
    }
    return scales;
  },
  forceTextsize() {
    this.storedSettings = _.set(
      this.storedSettings || {},
      "scale.forcedSize",
      this.forcedSize,
    );
    this.storedSettings = _.set(
      this.storedSettings || {},
      "scale.isTextsizeForced",
      this.isTextsizeForced,
    );
    CanvasSettingsState.updateAndPersist();
    NoteMenuState.updateComputedNoteConfig();
  },
  computeActiveScale() {
    const scale = structuredClone(toRaw(this.activeScale));
    if (!scale) return null;
    if (this.isTextsizeForced && this.forcedSize) {
      scale.props.textsize = this.forcedSize;
    }
    return scale;
  },
  updateOrCreateTextstyle(textstyle: Textstyle) {
    if (Facade.isInitialized && textstyle.filename) {
      const fontInfo = getFontForFileName(textstyle.filename);
      Facade.textStyles.create({
        name: textstyle.name,
        filename: textstyle.filename,
        typeface: fontInfo?.name,
        size: textstyle.size,
      });
    }
  },
});
